import { useCallback, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import type { PreviewEnvelope } from '../../types';
import { useLeaveTextEditing } from '../../use-leave-text-editing';
import { Eyebrow } from '../Eyebrow';
import { HintBox } from '../preview/HintBox';

/**
 * Screen 3 · Geschichte orchestrator (Bau-Spez v3.1 §6 lines 307-319).
 *
 * ═══ BLOCK 5 · PART 1 REWRITE (Sebastian ruling 2026-09-09) ══════════
 *
 * The screen is READ-ONLY (unchanged from Position-1 Commit-1). Block 5
 * Part 1 changes the layout:
 *
 *   - Eyebrow "EURE GESCHICHTE" + heading "So steht es in eurem Buch"
 *     + book title (envelope.title) in bold below.
 *   - The Klappzeile wrap around the twenty page rows is REMOVED. All
 *     twenty scenes render OPEN one below the other; no per-scene
 *     expander (Sebastian: "Part 1 shows them open because reading is
 *     the purpose there").
 *   - Each scene labelled "Seite N" (page number from envelope.pages,
 *     never "Szene A/B/C/D").
 *   - Weiter button relabelled "Weiter zu euren Vorschaubildern".
 *
 * The Erwartungskarte (summary body in a HintBox) stays at the top.
 * Klappzeile 2 (Dinge im Buch) remains hidden pending §5c-3 build.
 *
 * ═══ WEITER ══════════════════════════════════════════════════════════
 *
 * `leaveTextEditing` transitions text_editing → preview_rendering on
 * the server; PreviewScreen renders once the envelope refetch returns
 * the new status.
 */

const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';

const ERWARTUNGSKARTE_HEADING_ID = 'geschichte-erwartungskarte-heading';

export interface GeschichteScreenProps {
  readonly envelope: PreviewEnvelope;
  /** Re-fetch the envelope so BookRoute can re-dispatch on Weiter. */
  readonly onRefetch: () => void;
}

export function GeschichteScreen({ envelope, onRefetch }: GeschichteScreenProps) {
  const [weiterError, setWeiterError] = useState<boolean>(false);

  const leave = useLeaveTextEditing(envelope.bookOrderId);

  const handleWeiter = useCallback(async () => {
    setWeiterError(false);
    const leaveResult = await leave.leave();
    if (leaveResult.ok) {
      onRefetch();
    } else {
      setWeiterError(true);
    }
  }, [leave, onRefetch]);

  return (
    <div className="space-y-6 py-4">
      {/* Block 5 · 5.1 · Eyebrow + heading + book title. Book title
          renders in bold below the heading when non-empty (populated
          from envelope.title, which reads storyText.title || intake
          .titelwunsch per the Block 3 corrections). */}
      <div className="flex flex-col gap-3">
        <Eyebrow text={STRINGS.book.geschichte.eyebrow} />
        <h1 className="text-2xl font-semibold text-dream-navy">
          {STRINGS.book.geschichte.heading}
        </h1>
        {envelope.title.length > 0 && (
          <p className="text-lg font-semibold text-dream-navy">„{envelope.title}"</p>
        )}
      </div>

      <HintBox>
        <p id={ERWARTUNGSKARTE_HEADING_ID} className="text-base font-semibold text-dream-navy">
          {STRINGS.book.geschichte.erwartungskarteHeading}
        </p>
        {/* Position-5 Commit 6b — summary editing lives at
            summary_review, not here. Inline read-only note under the
            heading. */}
        <p className="mt-2 text-sm text-dream-navy/70">
          {STRINGS.book.summaryReview.storyScreen.readOnlyNote}
        </p>
        <p className="mt-3 text-base leading-relaxed text-dream-navy">{envelope.zusammenfassung}</p>
      </HintBox>

      {/* Block 5 · 5.2 · All twenty scenes open, one below the other.
          Each labelled "Seite N". No per-scene expander — reading is
          the purpose of this screen. */}
      <ol className="space-y-6">
        {envelope.pages.map((page) => (
          <li key={page.sceneId} className="space-y-2">
            <h2 className="text-base font-semibold text-dream-navy">
              {STRINGS.book.geschichte.pageRowHeading.replace('{n}', page.pageNo.toString())}
            </h2>
            <p className="text-base leading-relaxed text-dream-navy whitespace-pre-wrap">
              {page.text}
            </p>
          </li>
        ))}
      </ol>

      {weiterError && (
        <p role="alert" className="text-sm text-error text-center">
          {STRINGS.book.geschichte.weiterPhase2Error}
        </p>
      )}

      <button type="button" onClick={handleWeiter} disabled={leave.leaving} className={BUTTON_GOLD}>
        {STRINGS.book.geschichte.weiterCta}
      </button>
    </div>
  );
}
